// src/pages/HraAnalyticsGlobalPage.jsx
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import BarChartComponent from "../../components/results/barChartComponent";
import analyticsApi from "../../api/analyticsApi";
import usersApi from "../../api/usersApi";
import companiesApi from "../../api/companiesApi";
import responsesApi from "../../api/responsesApi";

import ageRanges from "../../data/ageRanges";
import mexicoStates from "../../data/mexicoStates";
import workScheme from "../../data/workScheme";
import biologicalSexList from "../../data/biologicalSex";
import styles from "./HraAnalyticsPage.module.css";

const formatDate = (iso) => {
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const HraAnalyticsGlobalPage = () => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [initialTotal, setInitialTotal] = useState(null);
  const [loading, setLoading] = useState(true);

  const [companies, setCompanies] = useState([]);
  const [responsesList, setResponsesList] = useState([]);
  const [loadingResponses, setLoadingResponses] = useState(true);

  const [searchParams, setSearchParams] = useSearchParams();

  // extraemos filtros de la URL
  const filters = {
    ageRange:       searchParams.get("ageRange")       || "",
    residenceState: searchParams.get("residenceState") || "",
    workScheme:     searchParams.get("workScheme")     || "",
    biologicalSex:  searchParams.get("biologicalSex")  || "",
    companyId:      searchParams.get("companyId")      || ""
  };

  const handleFilterChange = (e) => {
    const newParams = new URLSearchParams(searchParams);
    if (e.target.value) {
      newParams.set(e.target.name, e.target.value);
    } else {
      newParams.delete(e.target.name);
    }
    setSearchParams(newParams);
  };

  // fetch global analytics
  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true);
      try {
        const data = await analyticsApi.getHraGlobalAnalytics(filters);
        if (initialTotal === null && data?.totalRespondents) {
          setInitialTotal(data.totalRespondents);
        }
        setAnalyticsData(data);
      } catch (err) {
        console.error("Error cargando datos analíticos globales:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, [searchParams]);

  // cargamos empresas una vez
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const data = await companiesApi.getCompanies();
        setCompanies(data);
      } catch (err) {
        console.error("Error cargando empresas:", err);
      }
    };
    fetchCompanies();
  }, []);

  // cargamos respuestas HRA cuando cambia companyId
  useEffect(() => {
    const fetchResponses = async () => {
      setLoadingResponses(true);
      try {
        const data = await usersApi.getHRAResponses(filters.companyId);
        setResponsesList(data.responses);
      } catch (err) {
        console.error("Error cargando HRA responses:", err);
      } finally {
        setLoadingResponses(false);
      }
    };
    fetchResponses();
  }, [filters.companyId]);

  const handleDelete = async (responseId) => {
    try {
      await responsesApi.deleteHRAResponse(responseId);
      setResponsesList(prev =>
        prev.filter(r => r.responseId !== responseId)
      );
      alert("✅ La respuesta ha sido eliminada correctamente.");
    } catch (err) {
      console.error("Error eliminando respuesta HRA:", err);
      alert("⚠️ Ocurrió un error al eliminar la respuesta.");
    }
  };

  const renderFilter = (label, name, options) => (
    <div className={styles.filterItem} key={name}>
      <label htmlFor={name} className={styles.filterLabel}>
        {label}
      </label>
      <select
        id={name}
        name={name}
        value={filters[name]}
        onChange={handleFilterChange}
        className={styles.select}
      >
        <option value="">Todos</option>
        {options.map((opt) =>
          typeof opt === "object" ? (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ) : (
            <option key={opt} value={opt}>
              {opt}
            </option>
          )
        )}
      </select>
    </div>
  );

  return (
    <div className={styles.page}>
      {initialTotal !== null && (
        <div className={styles.initialTotalCapsule}>
          Total inicial: <span>{initialTotal}</span>
        </div>
      )}

      <header className={styles.header}>
        <h2>Gráficas HRA Globales</h2>
        <p className={styles.subheader}>Global overview across all companies</p>
      </header>

      <section className={styles.filters}>
        {renderFilter("Rango de Edad", "ageRange", ageRanges)}
        {renderFilter("Estado de Residencia", "residenceState", mexicoStates)}
        {renderFilter("Esquema Laboral", "workScheme", workScheme)}
        {renderFilter("Sexo Biológico", "biologicalSex", biologicalSexList)}
      </section>

      {!loading && analyticsData && (
        <div className={styles.totalCapsule}>
          Total encuestados: <span>{analyticsData.totalRespondents}</span>
        </div>
      )}

      <section className={styles.content}>
        {loading ? (
          <p className={styles.message}>Cargando gráficas globales...</p>
        ) : !analyticsData ? (
          <p className={styles.message}>No se pudieron cargar los datos.</p>
        ) : (
          <div className={styles.charts}>
            <BarChartComponent
              data={analyticsData.metabolicRisk}
              title="Riesgo Metabólico"
            />
            <BarChartComponent
              data={analyticsData.nutritionalState}
              title="Estado Nutricional"
            />
            <BarChartComponent
              data={analyticsData.lifeStyle}
              title="Estilo de Vida"
            />
            <BarChartComponent
              data={analyticsData.physicalActivityEvaluation}
              title="Actividad Física"
            />
            <BarChartComponent
              data={analyticsData.workHappiness}
              title="Felicidad Laboral"
            />
            <BarChartComponent
              data={analyticsData.overallEvaluation}
              title="Evaluación General"
            />
          </div>
        )}
      </section>

      <hr className={styles.separator} />

      <section className={styles.filters}>
        <div className={styles.filterItem}>
          <label htmlFor="companyId" className={styles.filterLabel}>
            Empresa
          </label>
          <select
            id="companyId"
            name="companyId"
            value={filters.companyId}
            onChange={handleFilterChange}
            className={styles.select}
          >
            <option value="">Todas</option>
            {companies.map((c) => (
              <option key={c._id} value={c._id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      </section>

      <section className={styles.responsesSection}>
        {loadingResponses ? (
          <p className={styles.message}>Cargando respuestas...</p>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Cuestionario</th>
                <th>Fecha</th>
                <th>Borrar</th>
              </tr>
            </thead>
            <tbody>
              {responsesList.length === 0 ? (
                <tr>
                  <td colSpan={4} className={styles.message}>
                    No hay respuestas para mostrar.
                  </td>
                </tr>
              ) : (
                responsesList.map((r) => (
                  <tr key={r.responseId}>
                    <td>{r.username}</td>
                    <td>{r.questionnaireName}</td>
                    <td>{formatDate(r.timestamp)}</td>
                    <td>
                      <button
                        onClick={() => handleDelete(r.responseId)}
                        className={styles.deleteButton}
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
};

export default HraAnalyticsGlobalPage;
